import { useState, useRef, useEffect } from 'react';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { AppModal } from '../components/AppModal';
import { useBusinessNature } from '../hooks/useBusinessNature';
import { validateHsnSac, isServiceCode } from '../lib/gstValidation';

const Row = ({ label, children }: any) => (
  <div className="flex items-center mb-2">
    <span className="w-[180px] text-[13px] text-gray-800 flex-shrink-0">{label}</span>
    <span className="w-[14px] text-center font-bold text-gray-700 flex-shrink-0">:</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export const StockItemCreationView = ({ handleItemSelect, stockItemId, onClose, onSave }: any) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const nameInputRef  = useRef<HTMLInputElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);

  const { has, term } = useBusinessNature();
  const isPharmacy = has('batchTracking');

  const emptyForm = { name: '', stockGroupId: null as number | null, stockCategoryId: null as number | null, unitId: 0, openingBalance: 0, openingRate: 0, openingValue: 0, gstRate: isPharmacy ? 12 : 18, hsn: isPharmacy ? '3004' : '', reorderLevel: 0, minStockLevel: 0, manufacturer: '', saltComposition: '', schedule: 'none' as 'none' | 'H' | 'H1' | 'X' | 'G' | 'narcotics', mrp: 0, pack: '', rackLocation: '' };
  const [formData, setFormData] = useState(emptyForm);
  const [accepting, setAccepting] = useState(false);

  const stockGroups     = useLiveQuery(() => activeCompany ? db.stockGroups.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const stockCategories = useLiveQuery(() => activeCompany ? db.stockCategories.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const units           = useLiveQuery(() => activeCompany ? db.units.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);

  useEnterAsTab(containerRef);

  useEffect(() => {
    if (stockItemId) {
      db.stockItems.get(stockItemId).then(item => {
        if (item) setFormData({ name: item.name, stockGroupId: item.stockGroupId, stockCategoryId: item.stockCategoryId, unitId: item.unitId, openingBalance: item.openingBalance, openingRate: item.openingRate, openingValue: item.openingValue, gstRate: item.gstRate, hsn: item.hsn || '', reorderLevel: item.reorderLevel || 0, minStockLevel: item.minStockLevel || 0, manufacturer: (item as any).manufacturer || '', saltComposition: (item as any).saltComposition || '', schedule: (item as any).schedule || 'none', mrp: (item as any).mrp || 0, pack: (item as any).pack || '', rackLocation: (item as any).rackLocation || '' });
      });
    } else { setFormData(emptyForm); }
  }, [stockItemId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'd') { e.preventDefault(); e.stopPropagation(); handleDelete(); }
    };
    if (stockItemId) window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [stockItemId]);

  useEffect(() => {
    if (!accepting) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key.toLowerCase() === 'y') {
        e.preventDefault(); e.stopPropagation(); doSave();
      } else if (e.key === 'Escape' || e.key.toLowerCase() === 'n') {
        e.preventDefault(); e.stopPropagation(); setAccepting(false);
      }
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [accepting]);

  const calculateValue = (qty: number, rate: number) => qty * rate;

  const doSave = async () => {
    setAccepting(false);
    if (!activeCompany || !formData.name.trim()) { alert('Name is required'); return; }
    if (stockItemId) {
      await db.stockItems.update(stockItemId, { ...formData, name: formData.name.trim() });
      if (onClose) onClose(); else if (handleItemSelect) handleItemSelect({ name: 'Quit' });
    } else {
      const exists = await db.stockItems.where('[companyId+name]').equals([activeCompany.id!, formData.name.trim()]).first();
      if (exists) { alert('Already exists'); return; }
      const newItemId = await db.stockItems.add({ companyId: activeCompany.id!, ...formData, name: formData.name.trim() });
      if (onSave) {
        onSave({ id: newItemId, name: formData.name.trim(), rate: formData.openingRate || 0, stock: formData.openingBalance || 0 });
        if (onClose) onClose();
        return;
      }
      setFormData(emptyForm);
      nameInputRef.current?.focus();
    }
  };

  const handleSave = () => setAccepting(true);

  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = async () => {
    if (!stockItemId) return;
    try {
      const usedCount = await db.inventoryEntries.where('stockItemId').equals(stockItemId).count();
      if (usedCount > 0) { alert('Stock Item is used in vouchers. Cannot delete.'); return; }
      setPendingDelete(true);
    } catch (error: any) { alert(error?.message || 'Failed to delete stock item'); }
  };

  const confirmDelete = async () => {
    setPendingDelete(false);
    if (!stockItemId) return;
    try {
      await db.stockItems.update(stockItemId, { isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: 'System' });
      if (onClose) onClose(); else if (handleItemSelect) handleItemSelect({ name: 'Quit' });
    } catch (error: any) { alert(error?.message || 'Failed to delete stock item'); }
  };

  const quit = () => { if (onClose) onClose(); else if (handleItemSelect) handleItemSelect({ name: 'Quit' }); };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
      <div ref={containerRef} className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
        style={{ width: '760px', maxWidth: '98vw', maxHeight: '94vh' }}>

        <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
          <span className="w-1/3 text-left">{term('stockItem')} {stockItemId ? 'Alteration' : 'Creation'}</span>
          <span className="w-1/3 text-center uppercase">{activeCompany?.name || ''}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={quit}>✕</span>
        </div>

        <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb] flex-shrink-0 p-4">
          <div className="flex items-center w-full">
            <span className="w-[150px] flex-shrink-0">Name</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input ref={nameInputRef} type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="flex-1 max-w-[450px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold"
              autoFocus required />
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto bg-[#fdfdfd]">
          <Row label="Under (Stock Group)">
            <select value={formData.stockGroupId || ''} onChange={e => setFormData({ ...formData, stockGroupId: e.target.value ? parseInt(e.target.value) : null })}
              className="border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold w-full max-w-[280px]">
              <option value="">Primary</option>
              {stockGroups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </Row>
          <Row label="Category">
            <select value={formData.stockCategoryId || ''} onChange={e => setFormData({ ...formData, stockCategoryId: e.target.value ? parseInt(e.target.value) : null })}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[280px] focus:bg-yellow-50">
              <option value="">Not Applicable</option>
              {stockCategories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Row>
          <Row label="Units">
            <select value={formData.unitId || ''} onChange={e => setFormData({ ...formData, unitId: parseInt(e.target.value) || 0 })} required
              className="border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold w-full max-w-[200px]">
              <option value="">Not Applicable</option>
              {units?.map(u => <option key={u.id} value={u.id}>{u.symbol}</option>)}
            </select>
          </Row>
          <Row label="GST Rate (%)">
            <select value={formData.gstRate ?? 18} onChange={e => setFormData({ ...formData, gstRate: parseInt(e.target.value) })}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50">
              <option value={0}>0% (Exempt)</option>
              <option value={5}>5%</option>
              <option value={12}>12%</option>
              <option value={18}>18%</option>
              <option value={28}>28%</option>
            </select>
          </Row>
          <Row label="HSN / SAC Code">
            <div className="flex flex-col">
              <input
                type="text"
                maxLength={8}
                placeholder="e.g. 85171200"
                value={formData.hsn ?? ''}
                onChange={e => setFormData({ ...formData, hsn: e.target.value.replace(/\D/g, '') })}
                className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50"
              />
              {formData.hsn && (() => {
                const err = validateHsnSac(formData.hsn);
                if (err) return <span className="text-[10px] text-red-500 mt-0.5">⚠ {err}</span>;
                if (isServiceCode(formData.hsn)) return <span className="text-[10px] text-blue-500 mt-0.5">SAC (Service)</span>;
                return <span className="text-[10px] text-emerald-600 mt-0.5">✓ Valid HSN code</span>;
              })()}
            </div>
          </Row>
          <Row label="Reorder Level">
            <input type="number" value={formData.reorderLevel} onChange={e => setFormData({ ...formData, reorderLevel: parseFloat(e.target.value) || 0 })}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[100px] focus:bg-yellow-50" />
          </Row>
          <Row label="Min Stock Level">
            <input type="number" value={formData.minStockLevel} onChange={e => setFormData({ ...formData, minStockLevel: parseFloat(e.target.value) || 0 })}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[100px] focus:bg-yellow-50" />
          </Row>

          {/* Pharmacy-specific fields */}
          {isPharmacy && (
            <div className="mt-4 border-t border-dashed border-[#c8d4d4] pt-4">
              <div className="font-bold underline underline-offset-2 mb-3 text-black text-[12.5px]" style={{ color: '#059669' }}>
                💊 {term('stockItem')} Details
              </div>
              <Row label="Manufacturer">
                <input type="text" value={formData.manufacturer} onChange={e => setFormData({ ...formData, manufacturer: e.target.value })}
                  placeholder="e.g. Cipla, Sun Pharma"
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[280px] focus:bg-yellow-50" />
              </Row>
              <Row label="Salt / Composition">
                <input type="text" value={formData.saltComposition} onChange={e => setFormData({ ...formData, saltComposition: e.target.value })}
                  placeholder="e.g. Paracetamol 500mg"
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[280px] focus:bg-yellow-50" />
              </Row>
              <Row label="Pack Size">
                <input type="text" value={formData.pack} onChange={e => setFormData({ ...formData, pack: e.target.value })}
                  placeholder="e.g. 1x10, 1x15, 60ml"
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[120px] focus:bg-yellow-50" />
              </Row>
              <Row label="MRP (₹)">
                <input type="number" step="0.01" value={formData.mrp || ''} onChange={e => setFormData({ ...formData, mrp: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[120px] focus:bg-yellow-50 text-right" />
              </Row>
              <Row label="Drug Schedule">
                <select value={formData.schedule} onChange={e => setFormData({ ...formData, schedule: e.target.value as any })}
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[160px] focus:bg-yellow-50">
                  <option value="none">None (OTC)</option>
                  <option value="H">Schedule H</option>
                  <option value="H1">Schedule H1</option>
                  <option value="X">Schedule X</option>
                  <option value="G">Schedule G</option>
                  <option value="narcotics">Narcotics</option>
                </select>
              </Row>
              <Row label="Rack / Shelf Location">
                <input type="text" value={formData.rackLocation} onChange={e => setFormData({ ...formData, rackLocation: e.target.value })}
                  placeholder="e.g. Rack 3, Shelf B"
                  className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50" />
              </Row>
            </div>
          )}

          <div className="mt-4 border-t border-dashed border-[#c8d4d4] pt-4">
            <div className="font-bold underline underline-offset-2 mb-3 text-black text-[12.5px]">Opening Balance</div>
            <div className="flex gap-6">
              <div className="flex flex-col">
                <label className="text-[11px] text-gray-500 mb-1">Quantity</label>
                <input type="number" value={formData.openingBalance}
                  onChange={e => { const qty = parseFloat(e.target.value) || 0; setFormData({ ...formData, openingBalance: qty, openingValue: calculateValue(qty, formData.openingRate) }); }}
                  className="border border-[#a0c5e8] bg-[#fff9c4] px-2 py-[2px] outline-none w-[120px] text-right" />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] text-gray-500 mb-1">Rate</label>
                <input type="number" value={formData.openingRate}
                  onChange={e => { const rate = parseFloat(e.target.value) || 0; setFormData({ ...formData, openingRate: rate, openingValue: calculateValue(formData.openingBalance, rate) }); }}
                  className="border border-[#a0c5e8] bg-[#fff9c4] px-2 py-[2px] outline-none w-[120px] text-right" />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] text-gray-500 mb-1">Value</label>
                <input type="number" value={formData.openingValue} readOnly
                  className="border border-gray-200 bg-gray-100 px-2 py-[2px] outline-none w-[120px] text-right text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0">
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleSave} tabIndex={-1}
              className="px-4 py-1 bg-[#1e8585] text-white font-bold rounded text-[11.5px] hover:bg-[#166e6e] shadow-sm">
              ✓ Accept (Ctrl+A)
            </button>
            {stockItemId && (
              <button type="button" onClick={handleDelete} tabIndex={-1}
                className="px-3 py-1 border border-gray-400 text-gray-700 bg-[#e0e0e0] hover:bg-[#d0d0d0] rounded text-[11.5px] font-semibold"
                title="Alt + D to Delete"><u>D</u>elete</button>
            )}
          </div>
          <span className="text-gray-500 italic text-[11px]">Enter = Next Field | Esc = Cancel</span>
        </div>

        {accepting && (
          <div className="absolute inset-0 bg-black/10 z-50 flex items-center justify-center">
            <div className="border-2 border-[#1e8585] bg-white px-16 py-6 shadow-[8px_8px_0_rgba(30,133,133,0.25)] text-center min-w-[200px] rounded-sm">
              <span className="block text-[17px] font-bold mb-4 text-black">Accept ?</span>
              <div className="font-bold text-[15px]">
                <span className="text-[#1e8585] cursor-pointer hover:underline" onClick={doSave}><u>Y</u>es</span>
                &nbsp;&nbsp;or&nbsp;&nbsp;
                <span className="text-gray-500 cursor-pointer hover:underline" onClick={() => setAccepting(false)}><u>N</u>o</span>
              </div>
            </div>
          </div>
        )}
        <button data-save-btn onClick={handleSave} className="hidden" />

        <AppModal
          open={pendingDelete}
          title="Delete Stock Item"
          type="warning"
          message="Are you sure you want to delete this stock item?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(false)}
        />
      </div>
    </div>
  );
};
